import { useMemo } from 'react';
import { BellOffIcon, PencilIcon } from 'lucide-react';
import { Skeleton } from '@rocco/components/skeleton';
import { localizeTime } from '~/modules/root';
import { useUserLoggedIn } from '~/modules/user';
import type { Conversation } from '../../types';
import { useChatBoxes } from '../../hooks/use-chat-boxes';
import { useChatInputDraft } from '../../hooks/use-chat-input-draft';
import {
    buildMentionLookup,
    getChatTitle,
    getPreviewMessage,
} from '../../utils';
import { ConversationAvatar } from '../avatar/ConversationAvatar';
import { NotificationBadge } from '../NotificationBadge';

/**
 * A conversation item in the chat inbox list.
 * @see {@link ChatInboxList}
 */
export function ChatInboxListItem({
    conversation,
    professionalID,
}: {
    /**
     * The conversation to display.
     */
    conversation: Conversation;
    /**
     * The professional ID of the company inbox context. If `null`, the
     * personal inbox context will be used.
     */
    professionalID: number | null;
}) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const { isChatBoxOpen, openChatBox } = useChatBoxes();
    const { draft } = useChatInputDraft(
        Number(conversation.ID),
        professionalID,
    );
    const isChatOpen = isChatBoxOpen(Number(conversation.ID), professionalID);
    const hasDraft = draft.message || draft.attachments.length > 0;
    const mentionLookup = useMemo(() => {
        return buildMentionLookup(conversation.Participants);
    }, [conversation]);
    const messagePreview = useMemo(() => {
        return getPreviewMessage(
            conversation.Messages.nodes[0],
            user.ID,
            professionalID,
            mentionLookup,
        );
    }, [user.ID, conversation, professionalID, mentionLookup]);
    const lastMessageDate = useMemo(() => {
        const lastMessage = conversation.Messages.nodes[0];
        if (!lastMessage) {
            return '';
        }
        const createdAt = localizeTime(lastMessage.Created);
        const isThisYear = createdAt.year() === new Date().getFullYear();
        return isThisYear
            ? createdAt.format('D MMM')
            : createdAt.format('D MMM YYYY');
    }, [conversation]);

    return (
        <li
            className="flex items-center gap-3 p-3 hover:bg-gray-100"
            role="button"
            aria-label="Open conversation"
            onClick={() => openChatBox(Number(conversation.ID), professionalID)}
        >
            <ConversationAvatar
                className="size-12"
                avatarClassName="size-12"
                fallbackClassName="!text-6"
                conversation={conversation}
                professionalID={professionalID}
                showActiveStatus
            />
            <div className="min-w-0 flex-1 space-y-1.5">
                <div className="truncate text-4 leading-none">
                    {getChatTitle(
                        conversation,
                        isLoggedIn ? user.ID : 0,
                        professionalID,
                    )}
                </div>
                <div className="line-clamp-2 min-h-[2.5em] text-3 leading-tight tracking-wide text-black/70">
                    {messagePreview}
                </div>
            </div>
            <div className="space-y-1.5">
                <div className="truncate text-right text-3.5 leading-none text-black/50">
                    {lastMessageDate}
                </div>
                <div className="flex min-h-[2.5em] items-center justify-end gap-2 text-3 leading-tight">
                    {hasDraft && !isChatOpen && (
                        <PencilIcon className="size-4 text-black/70" />
                    )}
                    {conversation.IsMuted && (
                        <BellOffIcon className="size-4 text-black/50" />
                    )}
                    <NotificationBadge
                        count={conversation.UnreadMessageCount}
                    />
                </div>
            </div>
        </li>
    );
}

/**
 * The loading skeleton for the chat inbox list item.
 * @see {@link ChatInboxListItem}
 */
export function ChatInboxListItemSkeleton() {
    return (
        <li className="flex items-center gap-3 rounded-lg p-3">
            <Skeleton className="size-12 rounded-full bg-gray-500" />
            <div className="min-w-0 flex-1 space-y-1.5">
                <Skeleton className="h-[1em] w-1/3 bg-gray-500 text-4 leading-none" />
                <div className="h-[2.5em] text-3 leading-tight">
                    <Skeleton className="h-1/2 w-2/3 bg-gray-500" />
                </div>
            </div>
        </li>
    );
}
